/**
 * Error humanizer (#1231) — guest-friendly replacements for the raw
 * Central/PMS error strings the booking and checkout widgets otherwise
 * surface verbatim (`err.message` from quote, coupon, reservation and
 * checkout-load failures: "Internal error occured. Please try later.",
 * "Invalid rate plan", "Property isnt available for reservation.", ...).
 *
 * Port of the toolkit's ErrorHumanizer module. The message table is
 * generated from `includes/ErrorHumanizer/ErrorMap.php` into
 * `error-map.generated.ts` (see `scripts/generate-error-map.ts`); this file
 * owns only the matching rules, which mirror the toolkit's REST filter and
 * JS MutationObserver:
 *
 *   1. trim the raw string;
 *   2. exact match wins — a specific message is never collapsed into a
 *      generic one;
 *   3. substring match ONLY for keys on the allowlisted prefix families
 *      (`cURL error `, `Card Error:`), whose real messages carry variable
 *      trailing detail, longest key first so "cURL error 60" is never
 *      served the "cURL error 6" copy. Every other key must match exactly,
 *      so a short generic key like "Not Available" can never clobber a
 *      longer, more specific message (the toolkit's F6 rule).
 *
 * Operator-editable overrides (the toolkit's admin map + REST endpoint) are
 * a follow-on — widget config vs Central-side is undecided (#1231 body).
 *
 * Display-only: nothing here touches what is requested or charged.
 */
import { ERROR_MAP_ENTRIES, ERROR_MAP_SUBSTRING_PREFIXES } from './error-map.generated'

/** Where the raw string came from — carried on the support debug line. */
export type ErrorSurface = 'quote' | 'reservation' | 'coupon' | 'checkout-load'

const EXACT = new Map<string, string>(ERROR_MAP_ENTRIES)

// Longest key first. The toolkit walks these in source order, where
// "cURL error 6" precedes "cURL error 60", so a real "cURL error 60: ..." message
// lands on the cURL 6 copy. Ordering by key length makes the most specific
// family win regardless of how the PHP table is laid out; ties keep source order.
// Deliberate divergence from the toolkit until it ships the same fix
// (hudsoncreativestudio/homerunner-toolkit#640); the guest copy itself is
// identical, only the overlap case differs.
const SUBSTRING_ENTRIES = ERROR_MAP_ENTRIES.filter(([original]) =>
  ERROR_MAP_SUBSTRING_PREFIXES.some((prefix) => prefix !== '' && original.startsWith(prefix)),
)
  .map((entry, index) => ({ entry, index }))
  .sort((a, b) => b.entry[0].length - a.entry[0].length || a.index - b.index)
  .map(({ entry }) => entry)

/**
 * Pure lookup: the guest-friendly replacement for `raw`, or `undefined` when
 * no mapping exists (the caller decides what to show then).
 */
export function lookupErrorMessage(raw: string): string | undefined {
  const text = raw.trim()
  if (!text) return undefined
  const exact = EXACT.get(text)
  if (exact !== undefined) return exact
  for (const [original, improved] of SUBSTRING_ENTRIES) {
    if (text.includes(original)) return improved
  }
  return undefined
}

/**
 * Guest-facing copy for an upstream error string.
 *
 * - mapped        → the toolkit's improved copy (raw string kept on
 *                   `console.debug` so support can still see what upstream said);
 * - unknown       → `raw`, unchanged (pass-through, never silently swallowed);
 * - empty/absent  → `fallback`, the caller's existing default copy.
 */
export function humanizeError(
  raw: string | null | undefined,
  surface: ErrorSurface,
  fallback: string,
): string {
  if (typeof raw !== 'string' || raw.trim() === '') return fallback
  const improved = lookupErrorMessage(raw)
  if (improved === undefined) return raw
  if (typeof console !== 'undefined' && typeof console.debug === 'function') {
    console.debug('[hr-error-map] rewrote upstream error', { surface, raw, improved })
  }
  return improved
}
